import React, { createContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import i18n from "../../i18n";
import { getCurrentUtilityId, isAdmin } from "../../guards/common/authentification-functions";
import { useGetBrandingQuery } from "../../service/branding-service";
import { useGetUtilityByIdQuery } from "../../service/utility-service";
import type { RootState } from "../../stores/store";
import { UserState } from "../../stores/slices/current-user-slice";
import { applyBrand, setFavicon } from "./applyBrand";
import BrandingLoadingDialog from "./BrandingLoadingDialog";
import { DEFAULT_COLORS, getBrand } from "./registry";
import { isBpsAdminPortal, resolveLocalTenantCode } from "./resolveTenant";
import { setTenantCurrency } from "../currency";
import type { TenantBrand, TenantColors } from "./types";

/**
 * Single app-wide tenant. Resolved from host/URL before login, then swapped to the
 * JWT tenant on login (utilityCode), and forced to "xepp" for the BPS admin (which
 * has no tenant of its own). The tenant key is always resolved from outside, so
 * `getBrand` never returns null. Colors are handled separately (see the branding
 * fetch below), unless the brand hardcodes its own — see TenantBrand.colors.
 */
export const TenantContext = createContext<TenantBrand>(getBrand(null));

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Subscribe to auth state as a re-render trigger on login/logout — the key
  // itself is read from the JWT (sessionStorage), which is not reactive on its own.
  const userState = useSelector((state: RootState) => state.user.userState);

  // The BPS admin portal is its own tenant — never the host's or the JWT's.
  // Recognised by URL before login (the login page is shared with the utility
  // admin) and by the JWT after, so the brand holds across the whole portal.
  const forcedKey = isBpsAdminPortal() ? "bps" : undefined;
  // UtilityId (GUID) from the JWT; drives the utilityCode lookup once authenticated.
  const utilityId = getCurrentUtilityId();
  const { data: utility } = useGetUtilityByIdQuery(utilityId ?? "", {
    skip: !!forcedKey || !utilityId,
  });

  // forced > JWT tenant > host/URL fallback (pre-auth). Kept as the raw backend code
  // rather than a registry key, so a tenant onboarded in the backend still reaches
  // the branding endpoint with its own code instead of the default tenant's.
  const tenantCode = useMemo(
    () =>
      forcedKey ??
      utility?.utilityCode?.toLowerCase() ??
      resolveLocalTenantCode(window.location.hostname, window.location.search),
    // Host is read once per load (no in-SPA reactivity needed for the embed ?tenant= param).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [forcedKey, utility?.utilityCode],
  );

  // Static brand assets only (app-info graphic, poweredBy) — a code with no
  // registry entry still falls back to the default brand for those. Name, logo and
  // colors come from the branding fetch below.
  const brand = useMemo(() => getBrand(tenantCode), [tenantCode]);

  // Backend-fetched brand colors; `data` stays undefined while loading or on
  // error, so colors below naturally falls back to DEFAULT_COLORS. Skipped for a
  // brand that carries its own colors — it is not a utility, so there is no
  // branding record to fetch and the request would only fail.
  const {
    data: branding,
    isFetching: isBrandingFetching,
    isError: isBrandingError,
    refetch: refetchBranding,
  } = useGetBrandingQuery(tenantCode, { skip: !!brand.colors });

  // Logout wipes the API cache in rootReducer (store.ts) without RTK Query's
  // resetApiState, so this query — mounted above the router, it outlives logout —
  // keeps its old subscription to an entry that no longer exists, and RTK Query
  // reports that as fetching forever. Refetch to recreate the entry. A refetch
  // while the query is already in flight is a no-op.
  useEffect(() => {
    if (userState !== UserState.LoggedIn && !brand.colors) {
      refetchBranding();
    }
  }, [userState, brand.colors, refetchBranding]);

  // The utility's configured currency rides on the same branding response.
  // Stored module-level so Money/formatCurrency resolve it outside React;
  // setTenantCurrency ignores undefined, so the NGN fallback holds until
  // (and unless) branding delivers a code.
  useEffect(() => {
    setTenantCurrency(branding?.currency?.code);
  }, [branding?.currency?.code]);

  const colors = useMemo<TenantColors>(
    () =>
      brand.colors ??
      (branding
        ? { primary: branding.appPrimaryColor, secondary: branding.appSecondaryColor, accent: branding.appAccentColor }
        : DEFAULT_COLORS),
    [brand.colors, branding],
  );

  // Backend-fetched brand name and logo, same fallback rule as colors: skipped
  // (keeps the static registry values) for a brand with hardcoded colors, otherwise
  // sourced from branding. Both logos are undefined while loading/erroring or when
  // none is configured, with no fallback — nothing is shown; the name keeps the
  // registry one, so the tab title is never left blank. Branding carries a single
  // image, so logoWithText resolves to that same logo rather than a separate wordmark.
  const resolvedBrand = useMemo<TenantBrand>(
    () => ({
      ...brand,
      name: brand.colors ? brand.name : branding?.name ?? brand.name,
      logo: brand.colors ? brand.logo : branding?.logo,
      logoWithText: brand.colors ? brand.logoWithText : branding?.logo,
      countryCode: branding?.country?.code?.trim().toLowerCase() || "ng",
      // undefined only while the fetch is still pending: a brand that skips the fetch, a
      // failed fetch and a response without the field all count as "answered", so the
      // flags provider stops waiting and falls back to the build's values.
      featureFlags: brand.colors || isBrandingError ? {} : branding ? branding.featureFlags ?? {} : undefined,
    }),
    [brand, branding, isBrandingError],
  );

  // Apply brand globally whenever the resolved tenant, its colors, its name or its logo change.
  useEffect(() => {
    applyBrand(brand, colors);
    document.title = resolvedBrand.name;
    // Unlike the in-page logo, the favicon falls back to the app's default icon.
    setFavicon(resolvedBrand.logo ?? "/favicon.ico");
  }, [brand, colors, resolvedBrand.name, resolvedBrand.logo]);

  // Auto-switch the UI language to the tenant's configured language once branding
  // resolves. No-op if absent (fetch failed / brand has no backend record) or
  // already active.
  useEffect(() => {
    if (branding?.language && branding.language !== i18n.language) {
      i18n.changeLanguage(branding.language);
    }
  }, [branding]);

  // Set on "Continue anyway"; re-armed whenever a new fetch attempt starts, so a retry
  // that fails again re-prompts instead of staying silently dismissed.
  const [retryDialogDismissed, setRetryDialogDismissed] = useState(false);
  useEffect(() => {
    if (isBrandingFetching) {
      setRetryDialogDismissed(false);
    }
  }, [isBrandingFetching]);

  return (
    <TenantContext.Provider value={resolvedBrand}>
      {children}
      {(isBrandingFetching || (isBrandingError && !retryDialogDismissed)) && (
        // The dialog's texts suspend until the customer namespace loads; it carries its own
        // <Suspense> boundaries, as this provider sits above the app's only one (index.tsx).
        <BrandingLoadingDialog
          isError={isBrandingError && !isBrandingFetching}
          onRetry={refetchBranding}
          onContinue={() => setRetryDialogDismissed(true)}
        />
      )}
    </TenantContext.Provider>
  );
};
